import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { formatSize, formatSystemText, formatTime, withinWindow } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatCardAction, ChatMessage, ChatMessageFile, ChatMessageKind, ChatReactionGroup, ChatVisitAction } from '../../chat.model';

/** Secção 12: o seletor rápido de reações (os seis do WhatsApp). */
const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export type ChatMessageStatus = 'read' | 'delivered' | 'sending' | 'failed' | null;

/** Secção 10: arrastar para a direita responde (como no WhatsApp). Distância máxima e a que dispara. */
const SWIPE_MAX = 72;
const SWIPE_TRIGGER = 56;

/** A citação desenhada por cima do texto de uma resposta. */
export interface ChatMessageQuote {
  messageId: string;
  name: string;
  text: string;
  deleted: boolean;
}

/** What the thread hands to the container when an image is clicked. */
export interface ChatImageOpen {
  message: ChatMessage;
  file: ChatMessageFile;
}

/**
 * One message, rendered by `kind` (contract 6.2): text (received / sent, with
 * the delivery state), note, system line, auto reply, property card. Text and
 * auto replies may carry attachments: images in a 1–4 grid (a click emits
 * `openImage` for the lightbox), PDFs as cards with icon, name and size.
 *
 * Grouping: the thread marks each message as the `groupStart` / `groupEnd` of
 * a run of the same author within a few minutes. Bubbles inside a run sit
 * closer, keep a flat corner on the author's side, the author name is shown
 * only on the first and the time / delivery state only on the last (or while
 * sending / failed). `showAvatar` draws a 28px avatar next to the first
 * received bubble of a run, with a spacer under it for the others.
 *
 * The delete action is an icon button that appears on hover / focus (always
 * on touch). Everything is derived in ngOnChanges; the component holds no
 * state of its own and talks to no service.
 */
@Component({
  selector: 'chat-message',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.css']
})
export class ChatMessageComponent implements OnChanges, OnInit, OnDestroy {
  @Input() message!: ChatMessage;
  /** The logged user sent it. */
  @Input() mine = false;
  /** Show the sender's name above a received bubble (conversations with more than two people). */
  @Input() showAuthor = false;
  /** First / last bubble of a run of the same author (both true for a lone message). */
  @Input() groupStart = true;
  @Input() groupEnd = true;
  /** Reserve the avatar column on received messages (the avatar itself only on `groupStart`). */
  @Input() showAvatar = false;
  @Input() avatarSrc?: string;
  @Input() avatarName?: string;
  /** Conversa com mais de duas pessoas: o nome de quem escreveu ao passar no avatar. */
  @Input() avatarTip?: string;
  /** Staff da conversa: pode marcar, cancelar e pedir a ficha (7.3). */
  @Input() canManageVisits = false;
  /** Staff com `canQualify`: editar as respostas e procurar imóveis a partir delas (7.3). */
  @Input() canQualify = false;
  /** O `messageId` do cartão cuja ação de um clique está a correr (spinner no botão). */
  @Input() actionBusy: string | null = null;
  /** Secção 9: janelas (segundos) de editar e de apagar para todos, de `chat_capabilities`. */
  @Input() editWindowSeconds = 0;
  @Input() deleteWindowSeconds = 0;
  /** Secção 10: a conversa aceita respostas (servidor com contrato ≥ 6, conversa não supervisionada). */
  @Input() canReply = false;
  /** O "eu" do chat: a citação de uma mensagem minha diz "Você". */
  @Input() meId?: string;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() retry = new EventEmitter<ChatMessage>();
  /** Secção 12: reagir (um emoji; `null` tira a reação própria). */
  @Output() react = new EventEmitter<{ message: ChatMessage; emoji: string | null }>();
  /** Ações dos cartões de visita (7.3), resolvidas pelo contentor. */
  @Output() visitAction = new EventEmitter<ChatVisitAction>();
  /** Ações dos cartões de qualificação e proposta (7.3). */
  @Output() cardAction = new EventEmitter<ChatCardAction>();
  /** O negócio da conversa (link do cartão de proposta). */
  @Input() dealId?: number | null;
  @Output() deleteMessage = new EventEmitter<ChatMessage>();
  /** Secção 9: "Editar" (o contentor abre o compositor em modo edição). */
  @Output() editMessage = new EventEmitter<ChatMessage>();
  /** Secção 10: "Responder" (botão ou arrastar para a direita) e o clique na citação. */
  @Output() reply = new EventEmitter<ChatMessage>();
  @Output() jumpTo = new EventEmitter<string>();
  @Output() deleteFile = new EventEmitter<{ message: ChatMessage; file: ChatMessageFile }>();
  @Output() openImage = new EventEmitter<ChatImageOpen>();

  kind: ChatMessageKind = 'text';
  time = '';
  status: ChatMessageStatus = null;
  statusLabel = '';
  text = '';
  systemText = '';
  /** Ícone da linha de sistema, por `code` (`strings.systemIcon`). */
  systemIcon = 'userplus';
  files: ChatMessageFile[] = [];
  images: ChatMessageFile[] = [];
  pdfs: ChatMessageFile[] = [];
  /** Mensagem de voz: o anexo de áudio e a duração (dos params). */
  audios: ChatMessageFile[] = [];
  voiceDuration?: number;
  canDelete = false;
  /** Secção 9: a minha mensagem de texto / nota, ainda dentro dos 15 min. */
  canEdit = false;
  /** Secção 9: apagada para todos (só o aviso) e editada ("Editada" junto da hora). */
  deleted = false;
  edited = false;
  deletedText = '';
  /** Secção 10: esta mensagem aceita resposta, e a citação que ela própria leva. */
  replyable = false;
  quote: ChatMessageQuote | null = null;
  /** Secção 12: reações da mensagem, a minha, e se se pode reagir (não a sistema, apagada ou a enviar). */
  reactions: ChatReactionGroup[] = [];
  myReaction: string | null = null;
  reactable = false;
  reactOpen = false;
  readonly quickEmojis = QUICK_EMOJIS;

  private swipe: { id: number; x: number; y: number; dx: number; active: boolean } | null = null;
  private readonly listeners: Array<[string, (e: any) => void]> = [];

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone, private cdr: ChangeDetectorRef) {}

  /**
   * Arrastar para a direita (toque, caneta ou rato) responde, como no WhatsApp.
   * Os eventos ficam FORA da zona do Angular: um `pointermove` por mensagem a
   * disparar deteção de alterações custava caro numa conversa longa. Só o
   * `reply` volta à zona. Um gesto mais vertical do que horizontal é deslocar o
   * fio e não conta; botões, links e campos dentro da mensagem não iniciam o gesto.
   */
  ngOnInit(): void {
    const el = this.host.nativeElement;
    const on = (type: string, fn: (e: any) => void) => {
      el.addEventListener(type, fn);
      this.listeners.push([type, fn]);
    };
    this.zone.runOutsideAngular(() => {
      on('pointerdown', (e: PointerEvent) => this.onSwipeStart(e));
      on('pointermove', (e: PointerEvent) => this.onSwipeMove(e));
      on('pointerup', (e: PointerEvent) => this.onSwipeEnd(e, true));
      on('pointercancel', (e: PointerEvent) => this.onSwipeEnd(e, false));
      on('lostpointercapture', (e: PointerEvent) => this.onSwipeEnd(e, true));
    });
  }

  ngOnDestroy(): void {
    const el = this.host.nativeElement;
    this.listeners.forEach(([type, fn]) => el.removeEventListener(type, fn));
  }

  private onSwipeStart(e: PointerEvent): void {
    if (!this.replyable || (e.pointerType === 'mouse' && e.button !== 0)) {
      return;
    }
    const target = e.target as HTMLElement | null;
    if (target && target.closest('button, a, input, textarea, select, label, [contenteditable]')) {
      return;
    }
    this.swipe = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, active: false };
  }

  private onSwipeMove(e: PointerEvent): void {
    const s = this.swipe;
    if (!s || e.pointerId !== s.id) {
      return;
    }
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!s.active) {
      if (Math.abs(dy) > 10 && Math.abs(dy) >= Math.abs(dx)) {
        this.swipe = null;
        return;
      }
      if (dx < 12 || dx < Math.abs(dy) * 1.5) {
        return;
      }
      s.active = true;
      const el = this.host.nativeElement;
      el.classList.add('chat-msg-host--swiping');
      try {
        el.setPointerCapture(e.pointerId);
      } catch (err) {
        /* sem captura o gesto continua enquanto o ponteiro estiver na mensagem */
      }
      const selection = window.getSelection ? window.getSelection() : null;
      if (selection) {
        selection.removeAllRanges();
      }
    }
    s.dx = Math.max(0, Math.min(dx, SWIPE_MAX));
    this.applySwipe(s.dx);
    e.preventDefault();
  }

  private onSwipeEnd(e: PointerEvent, commit: boolean): void {
    const s = this.swipe;
    if (!s || e.pointerId !== s.id) {
      return;
    }
    this.swipe = null;
    if (!s.active) {
      return;
    }
    const el = this.host.nativeElement;
    el.classList.remove('chat-msg-host--swiping');
    this.applySwipe(0);
    // O clique que vem a seguir ao gesto não abre a imagem nem o cartão.
    const swallow = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
    el.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => el.removeEventListener('click', swallow, { capture: true } as any), 0);
    if (commit && s.dx >= SWIPE_TRIGGER) {
      this.zone.run(() => this.reply.emit(this.message));
    }
  }

  private applySwipe(dx: number): void {
    const style = this.host.nativeElement.style;
    style.setProperty('--chat-swipe', dx + 'px');
    style.setProperty('--chat-swipe-p', String(Math.min(1, dx / SWIPE_TRIGGER)));
  }
  author = '';
  /** Time / state line under the bubble: last of the run, or while pending. */
  showMeta = true;
  /** Nota: o texto partido em pedaços, com as menções `@Nome` marcadas (7.1). */
  noteParts: { text: string; mention: boolean }[] = [];

  ngOnChanges(): void {
    const m = this.message;
    this.kind = (m && m.kind) || 'text';
    this.time = m ? formatTime(m.time) : '';
    this.text = (m && m.message) || '';
    this.files = (m && m.files ? m.files : []).filter(f => !f.isDelete);
    this.audios = this.files.filter(f => !!f.type && f.type.indexOf('audio/') === 0);
    this.images = this.files.filter(f => f.type !== 'application/pdf' && this.audios.indexOf(f) === -1);
    this.pdfs = this.files.filter(f => f.type === 'application/pdf');
    this.voiceDuration = m && m.params && m.params['voice'] ? Number(m.params['voice'].duration) || undefined : undefined;
    this.author = this.showAuthor && this.groupStart && m && m.userSender && m.userSender.name ? m.userSender.name : '';
    this.systemText = this.kind === 'system' && m && m.system
      ? formatSystemText(m.system.code, m.system.params, this.strings)
      : '';
    this.systemIcon = (this.kind === 'system' && m && m.system && this.strings.systemIcon[m.system.code]) || 'userplus';
    this.noteParts = this.kind === 'note' ? this.splitMentions(this.text, (m && m.mentions) || []) : [];
    this.status = this.computeStatus();
    this.statusLabel = this.labelFor(this.status);
    this.showMeta = this.groupEnd || this.status === 'sending' || this.status === 'failed';
    // Secção 9 (regras do WhatsApp): só o autor, só uma mensagem já assente; apagar
    // para todos até 60 h (nunca registos do CRM), editar texto / nota até 15 min.
    this.deleted = !!(m && m.deleted);
    this.edited = !this.deleted && !!(m && m.editedAt);
    this.deletedText = this.mine ? this.strings.messageDeletedMine
      : this.kind === 'note' ? this.strings.noteDeleted : this.strings.messageDeleted;
    const settled = this.mine && !!m && !!m.messageId && !m.sendState && !this.deleted;
    this.canDelete = settled && this.kind !== 'system' && this.kind !== 'visit' && this.kind !== 'visit_form' && this.kind !== 'proposal'
      && withinWindow(m.time, this.deleteWindowSeconds);
    this.canEdit = settled && (this.kind === 'text' || this.kind === 'note') && !!this.text && withinWindow(m.time, this.editWindowSeconds);
    // Secção 10: responde-se a tudo o que já tem id, menos linhas de sistema e mensagens apagadas.
    this.replyable = this.canReply && !!m && !!m.messageId && !m.sendState && !this.deleted && this.kind !== 'system';
    this.quote = this.buildQuote(m);
    // Secção 12: reações (as mais usadas primeiro) e a minha.
    this.reactions = (m && !this.deleted && m.reactions) || [];
    const mine = this.meId ? this.reactions.find(g => g.userAppIds.indexOf(this.meId!) > -1) : undefined;
    this.myReaction = mine ? mine.emoji : null;
    this.reactable = !!m && !!m.messageId && !m.sendState && !this.deleted && this.kind !== 'system';
    if (!this.reactable) {
      this.reactOpen = false;
    }
  }

  /** Secção 12: o seletor rápido de reações (6 emojis), nas ações da mensagem. */
  toggleReactPicker(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.reactOpen = !this.reactOpen;
  }

  pickReaction(emoji: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.reactOpen = false;
    this.react.emit({ message: this.message, emoji: this.myReaction === emoji ? null : emoji });
  }

  /** Clicar numa reação: junta-se a ela, ou tira a própria se já era a minha. */
  onReactionChip(group: ChatReactionGroup, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.reactable) {
      return;
    }
    this.react.emit({ message: this.message, emoji: this.myReaction === group.emoji ? null : group.emoji });
  }

  isMyReaction(group: ChatReactionGroup): boolean {
    return !!this.meId && group.userAppIds.indexOf(this.meId) > -1;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.reactOpen) {
      this.reactOpen = false;
      this.cdr.markForCheck();
    }
  }

  trackByReaction = (_i: number, g: ChatReactionGroup) => g.emoji;

  /** A citação: quem escreveu ("Você" se fui eu) e o texto atual, ou o nome do cartão / anexo, ou "apagada". */
  private buildQuote(m: ChatMessage | undefined): ChatMessageQuote | null {
    const r = m && !this.deleted ? m.replyTo : undefined;
    if (!r || !r.messageId) {
      return null;
    }
    const name = r.authorId && r.authorId === this.meId ? this.strings.participantYou : (r.authorName || '');
    let text = '';
    if (r.deleted) {
      text = this.strings.messageDeleted;
    } else if (r.text) {
      text = r.text;
    } else if (r.kind && r.kind !== 'text' && this.strings.previewByKind[r.kind]) {
      text = this.strings.previewByKind[r.kind];
    } else if (r.files && r.files > 0) {
      text = r.files > 1 ? this.strings.attachmentsPreview.replace('{n}', String(r.files)) : this.strings.attachmentPreview;
    }
    return { messageId: r.messageId, name, text, deleted: !!r.deleted };
  }

  emitVisit(type: ChatVisitAction['type'], when?: string): void {
    this.visitAction.emit({ type, message: this.message, when });
  }

  emitCard(type: ChatCardAction['type']): void {
    this.cardAction.emit({ type, message: this.message });
  }

  /** "Olá @Rita, vês isto?" -> [texto, menção, texto], pelos nomes mencionados (os mais longos primeiro). */
  private splitMentions(text: string, mentions: { id: string; name?: string }[]): { text: string; mention: boolean }[] {
    const names = mentions.map(m => m.name).filter((n): n is string => !!n).sort((a, b) => b.length - a.length);
    if (!text || names.length === 0) {
      return text ? [{ text, mention: false }] : [];
    }
    const parts: { text: string; mention: boolean }[] = [];
    let rest = text;
    while (rest.length > 0) {
      let best = -1;
      let bestName = '';
      for (const name of names) {
        const idx = rest.indexOf('@' + name);
        if (idx > -1 && (best === -1 || idx < best)) {
          best = idx;
          bestName = name;
        }
      }
      if (best === -1) {
        parts.push({ text: rest, mention: false });
        break;
      }
      if (best > 0) {
        parts.push({ text: rest.slice(0, best), mention: false });
      }
      parts.push({ text: bestName, mention: true });
      rest = rest.slice(best + bestName.length + 1);
    }
    return parts;
  }

  /** Image: server name (full URL) once uploaded, local data URL while pending. */
  imageSrc(f: ChatMessageFile): string | undefined {
    return f.name ? f.name : f.nameTemp;
  }

  fileSize(f: ChatMessageFile): string {
    return formatSize(f.size, f.sizeText);
  }

  get avatarLabel(): string | undefined {
    return this.avatarName || (this.message && this.message.userSender ? this.message.userSender.name : undefined);
  }

  trackByFile = (i: number, f: ChatMessageFile) => f.id || f.nameTemp || i;

  private computeStatus(): ChatMessageStatus {
    const m = this.message;
    if (!this.mine || !m || this.kind === 'system' || m.deleted) {
      return null;
    }
    if (m.sendState === 'failed') {
      return 'failed';
    }
    if (m.sendState === 'sending' || !m.messageId) {
      return 'sending';
    }
    if (this.kind === 'note') {
      // Nobody "reads" a note; the server did accept it.
      return null;
    }
    return m.unread === false ? 'read' : 'delivered';
  }

  private labelFor(status: ChatMessageStatus): string {
    switch (status) {
      case 'read': return this.strings.read;
      case 'delivered': return this.strings.delivered;
      case 'sending': return this.strings.sending;
      case 'failed': return this.strings.failed;
      default: return '';
    }
  }
}
